import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, CheckCheck, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/lib/api';

/**
 * NotificationBell — owner alert bell.
 *
 * Reads the owner's in-app alerts from GET /api/owner/alerts (which also kicks
 * the budget-milestone + interest sweep server-side, so opening the bell
 * surfaces freshly-crossed thresholds). Shows an unread badge, a dropdown of
 * recent alerts, per-alert dismiss, and "mark all read". Polls every 60s.
 *
 * variant="header"  → compact icon button + absolute dropdown (page headers).
 * variant="sidebar" → full-width nav row + fixed panel (global left rail).
 */
function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const KIND_ACCENT = (type = '') => {
  const t = type.toLowerCase();
  if (t.includes('overdrawn') || t.includes('overdue')) return '#dc2626';
  if (t.includes('budget') || t.includes('contingency')) return '#d97706';
  if (t.includes('start') || t.includes('schedule')) return '#2563eb';
  if (t.includes('interest') || t.includes('payment')) return '#059669';
  return '#64748b';
};

export default function NotificationBell({ userId, variant = 'header', expanded = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);
  const isSidebar = variant === 'sidebar';

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await apiService.getOwnerAlerts(userId);
      setAlerts(res.data?.alerts || []);
      setUnread(res.data?.unread_count || 0);
    } catch (_e) {
      /* non-blocking */
    }
  }, [userId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      const unreadIds = alerts.filter((a) => !a.is_read).map((a) => a.id);
      setUnread(0);
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
      await Promise.all(unreadIds.map((id) => apiService.markOwnerAlertRead(id, userId).catch(() => {})));
    }
  };

  const dismiss = async (e, id) => {
    e.stopPropagation();
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    try { await apiService.dismissOwnerAlert(id, userId); } catch (_e) { /* ignore */ }
  };

  const markAllRead = async () => {
    const unreadIds = alerts.filter((a) => !a.is_read).map((a) => a.id);
    setUnread(0);
    setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    await Promise.all(unreadIds.map((id) => apiService.markOwnerAlertRead(id, userId).catch(() => {})));
  };

  const onAlertClick = (a) => {
    const pid = a.project_id || a.reference_id;
    if (pid && a.reference_type !== 'none') {
      setOpen(false);
      navigate(`/project/${pid}`);
    }
  };

  const Panel = (
    <div
      className={
        isSidebar
          ? 'fixed bottom-4 w-[340px] max-w-[88vw] bg-white rounded-xl border border-slate-200 shadow-2xl z-[80] overflow-hidden'
          : 'absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden'
      }
      style={isSidebar ? { left: expanded ? 268 : 88 } : undefined}
      data-testid="notification-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="font-bold text-slate-900 text-sm">Notifications</span>
        {alerts.some((a) => !a.is_read) && (
          <button
            onClick={markAllRead}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="notification-mark-all-read"
          >
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {alerts.length === 0 && (
          <div className="py-10 px-4 text-center" data-testid="notification-empty">
            <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium text-slate-600">You're all caught up</p>
            <p className="text-xs text-slate-400 mt-1">Budget, schedule, and draw alerts will show up here.</p>
          </div>
        )}
        {alerts.map((a) => (
          <button
            key={a.id}
            onClick={() => onAlertClick(a)}
            className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${a.is_read ? '' : 'bg-blue-50/40'}`}
            data-testid={`notification-item-${a.id}`}
          >
            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: KIND_ACCENT(a.alert_type) }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900 truncate">{a.title || 'Notification'}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => dismiss(e, a.id)}
                  className="text-slate-300 hover:text-slate-600 flex-shrink-0 cursor-pointer"
                  data-testid={`notification-dismiss-${a.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </span>
              </div>
              {a.message && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.message}</p>}
              <p className="text-[11px] text-slate-400 mt-1">{timeAgo(a.created_at)}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  if (isSidebar) {
    return (
      <div ref={wrapRef}>
        <button
          type="button"
          onClick={openPanel}
          className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-700/40 hover:text-white transition-all ${expanded ? '' : 'justify-center'}`}
          title={!expanded ? 'Notifications' : undefined}
          data-testid="notification-bell-btn"
        >
          <span className="relative flex-shrink-0">
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span
                className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
                data-testid="notification-unread-badge"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </span>
          {expanded && <span className="text-sm">Notifications</span>}
        </button>
        {open && Panel}
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={openPanel}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:border-slate-300 transition"
        title="Notifications"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && Panel}
    </div>
  );
}
